/**
 * Partner Linking Hook - Manages partner invites and connections
 * Uses email-based invites to connect partners
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface PartnerLink {
  id: string;
  requester_id: string;
  partner_id: string | null;
  partner_email: string;
  link_type: string;
  status: string;
  created_at: string;
  accepted_at: string | null;
  partner_display_name?: string;
}

export function usePartnerLink() {
  const [partnerLink, setPartnerLink] = useState<PartnerLink | null>(null);
  const [pendingInvite, setPendingInvite] = useState<PartnerLink | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch current user and their partner link status
  useEffect(() => {
    const fetchPartnerStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsLoading(false);
          return;
        }
        
        setUserId(user.id);
        setUserEmail(user.email || null);

        // Check for accepted partner link (as requester or partner)
        const { data: acceptedLinks } = await supabase
          .from('partner_links')
          .select('*')
          .eq('status', 'accepted');

        const acceptedLink = acceptedLinks?.find(
          link => link.requester_id === user.id || link.partner_id === user.id
        );

        if (acceptedLink) {
          // Get partner's display name
          const partnerId = acceptedLink.requester_id === user.id 
            ? acceptedLink.partner_id 
            : acceptedLink.requester_id;
          
          if (partnerId) {
            const { data: partnerProfile } = await supabase
              .from('profiles')
              .select('display_name')
              .eq('user_id', partnerId)
              .maybeSingle();

            setPartnerLink({
              ...acceptedLink,
              partner_display_name: partnerProfile?.display_name || 'Partner'
            });
          }
        }

        // Check for pending invites TO this user (by email)
        if (user.email) {
          const { data: pendingInvites } = await supabase
            .from('partner_links')
            .select('*')
            .eq('partner_email', user.email)
            .eq('status', 'pending');

          if (pendingInvites && pendingInvites.length > 0) {
            const invite = pendingInvites[0];
            // Get inviter's display name
            const { data: inviterProfile } = await supabase
              .from('profiles')
              .select('display_name')
              .eq('user_id', invite.requester_id)
              .maybeSingle();

            setPendingInvite({
              ...invite,
              partner_display_name: inviterProfile?.display_name || 'Someone'
            });
          }
        }
      } catch (error) {
        console.error('Error fetching partner status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPartnerStatus();
  }, []);

  // Create a new invite by email
  const createInvite = useCallback(async (
    partnerEmail: string, 
    linkType: string = 'romantic'
  ): Promise<boolean> => {
    if (!userId) return false;

    // Basic email validation
    if (!partnerEmail || !partnerEmail.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return false;
    }

    // Can't invite yourself
    if (partnerEmail.toLowerCase() === userEmail?.toLowerCase()) {
      toast({
        title: "That's your email",
        description: "You can't invite yourself!",
        variant: "destructive"
      });
      return false;
    }

    try {
      // Check if user already has an active link or pending invite they sent
      const { data: existingLinks } = await supabase
        .from('partner_links')
        .select('*')
        .eq('requester_id', userId)
        .in('status', ['pending', 'accepted']);

      if (existingLinks && existingLinks.length > 0) {
        const existing = existingLinks[0];
        if (existing.status === 'accepted') {
          toast({
            title: "Already linked",
            description: "You're already connected with a partner.",
            variant: "destructive"
          });
          return false;
        }
        // Already have a pending invite
        toast({
          title: "Invite pending",
          description: `You already have a pending invite to ${existing.partner_email}.`,
          variant: "destructive"
        });
        return false;
      }

      // Insert the invite record
      const { error: insertError } = await supabase
        .from('partner_links')
        .insert({
          requester_id: userId,
          partner_email: partnerEmail.toLowerCase().trim(),
          link_type: linkType,
          status: 'pending'
        });

      if (insertError) throw insertError;

      // Send email notification via edge function
      try {
        const { error: emailError } = await supabase.functions.invoke('send-partner-invite', {
          body: {
            partnerEmail: partnerEmail.toLowerCase().trim(),
            relationshipType: linkType
          }
        });

        if (emailError) {
          console.warn('Email send failed, but invite was created:', emailError);
        }
      } catch (emailErr) {
        console.warn('Email notification failed:', emailErr);
        // Don't fail the whole operation if email fails
      }

      toast({
        title: "Invite sent!",
        description: `An invite has been sent to ${partnerEmail}. They'll receive an email notification.`
      });

      return true;
    } catch (error) {
      console.error('Error creating invite:', error);
      toast({
        title: "Error",
        description: "Could not send invite. Please try again.",
        variant: "destructive"
      });
      return false;
    }
  }, [userId, userEmail, toast]);

  // Accept a pending invite
  const acceptPendingInvite = useCallback(async (): Promise<boolean> => {
    if (!pendingInvite || !userId) return false;

    try {
      const { error } = await supabase
        .from('partner_links')
        .update({
          partner_id: userId,
          status: 'accepted',
          accepted_at: new Date().toISOString()
        })
        .eq('id', pendingInvite.id);

      if (error) throw error;

      setPartnerLink({
        ...pendingInvite,
        partner_id: userId,
        status: 'accepted',
        accepted_at: new Date().toISOString()
      });
      setPendingInvite(null);

      toast({
        title: "Connected!",
        description: `You're now linked with ${pendingInvite.partner_display_name}!`
      });

      return true;
    } catch (error) {
      console.error('Error accepting invite:', error);
      toast({
        title: "Error",
        description: "Could not accept invite. Please try again.",
        variant: "destructive"
      });
      return false;
    }
  }, [pendingInvite, userId, toast]);

  // Decline a pending invite
  const declinePendingInvite = useCallback(async (): Promise<boolean> => {
    if (!pendingInvite) return false;

    try {
      const { error } = await supabase
        .from('partner_links')
        .update({ status: 'declined' })
        .eq('id', pendingInvite.id);

      if (error) throw error;

      setPendingInvite(null);

      toast({
        title: "Invite declined",
        description: "The invite has been declined."
      });

      return true;
    } catch (error) {
      console.error('Error declining invite:', error);
      return false;
    }
  }, [pendingInvite, toast]);

  // Unlink from partner
  const unlinkPartner = useCallback(async (): Promise<boolean> => {
    if (!partnerLink) return false;

    try {
      const { error } = await supabase
        .from('partner_links')
        .delete()
        .eq('id', partnerLink.id);

      if (error) throw error;

      setPartnerLink(null);

      toast({
        title: "Unlinked",
        description: "You're no longer connected with your partner."
      });

      return true;
    } catch (error) {
      console.error('Error unlinking partner:', error);
      toast({
        title: "Error",
        description: "Could not unlink. Please try again.",
        variant: "destructive"
      });
      return false;
    }
  }, [partnerLink, toast]);

  // Cancel a pending invite you sent
  const cancelInvite = useCallback(async (): Promise<boolean> => {
    if (!userId) return false;

    try {
      const { error } = await supabase
        .from('partner_links')
        .delete()
        .eq('requester_id', userId)
        .eq('status', 'pending');

      if (error) throw error;

      toast({
        title: "Invite cancelled",
        description: "Your invite has been cancelled."
      });

      return true;
    } catch (error) {
      console.error('Error cancelling invite:', error);
      return false;
    }
  }, [userId, toast]);

  return {
    partnerLink,
    pendingInvite,
    isLoading,
    isLinked: !!partnerLink,
    hasPendingInvite: !!pendingInvite,
    userEmail,
    createInvite,
    acceptPendingInvite,
    declinePendingInvite,
    unlinkPartner,
    cancelInvite
  };
}
